import React, { useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import {
  LOGO_ACCEPT_ATTRIBUTE,
  validateLogo,
} from "../data/uploadRules";
import { vendorLogoUrl } from "./VendorLogo";

// Optional logo on a subprocessor added by hand. The file is uploaded as
// soon as it is chosen; the path is saved with the rest of the form.
const VendorLogoField = ({
  label,
  hint,
  logoPath,
  error,
  upload,
  onChange,
  onUploading,
}) => {
  const theme = useTheme();
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState("");
  const url = vendorLogoUrl(logoPath);
  const message = localError || error;

  const handleFile = async (file) => {
    if (!file) return;
    const problem = validateLogo(file);
    if (problem) {
      setLocalError(problem);
      return;
    }
    setBusy(true);
    onUploading?.(true);
    setLocalError("");
    try {
      onChange(await upload(file));
    } catch (e) {
      setLocalError(e.message || "Could not upload the logo. Try again.");
    } finally {
      setBusy(false);
      onUploading?.(false);
    }
  };

  return (
    <Box>
      <Typography
        component="span"
        sx={{
          display: "block",
          fontSize: "0.875rem",
          fontWeight: 600,
          color: theme.customColors?.text?.strong ?? "#374151",
          mb: 0.75,
        }}
      >
        {label}
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box
          sx={{
            width: 56,
            height: 56,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
            border: `1px dashed ${theme.customColors.border.dashed}`,
            backgroundColor: theme.customColors.background.infoBox,
            overflow: "hidden",
          }}
        >
          {url ? (
            <Box
              component="img"
              src={url}
              alt=""
              sx={{ width: "72%", height: "72%", objectFit: "contain" }}
            />
          ) : (
            <ImageOutlinedIcon sx={{ color: theme.customColors.icons.secondary }} />
          )}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1}>
            <Button
              component="label"
              variant="outlined"
              size="small"
              disabled={busy}
              sx={{ textTransform: "none", borderRadius: "8px" }}
            >
              {busy ? "Uploading…" : url ? "Replace logo" : "Upload logo"}
              <input
                hidden
                type="file"
                aria-label="Upload vendor logo"
                accept={LOGO_ACCEPT_ATTRIBUTE}
                onChange={(e) => {
                  handleFile(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </Button>
            {url && (
              <Button
                size="small"
                disabled={busy}
                onClick={() => {
                  setLocalError("");
                  onChange("");
                }}
                sx={{ textTransform: "none" }}
              >
                Remove
              </Button>
            )}
          </Stack>
          <Typography
            variant="caption"
            role={message ? "alert" : undefined}
            sx={{
              display: "block",
              mt: 0.5,
              color: message
                ? theme.customColors.status.error.text
                : theme.customColors.text.secondary,
            }}
          >
            {message || hint}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

export default VendorLogoField;
